import type { AddressFormData } from '@choco-admin/types';

/**
 * Add Google Places script tag to document head if not already present
 */
export const maybeAddGooglePlacesScriptTag = ({
  apiKey,
}: {
  apiKey: string;
}) => {
  if (!apiKey) return;

  // Check if script is already loaded
  if (document.querySelector('script[src*="maps.googleapis.com"]')) {
    return;
  }

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

// TODO: Import proper types when Google Places package is available
interface PlaceDetails {
  formatted_address?: string;
  name: string;
  place_id: string;
  geometry?: {
    location: {
      lat(): number;
      lng(): number;
    };
  };
  utc_offset_minutes?: number;
  address_components?: Array<{
    types: string[];
    long_name: string;
    short_name: string;
  }>;
}

type AddressComponents = Exclude<PlaceDetails['address_components'], undefined>;

const getAddressComponentsTypes = (addressComponents: AddressComponents) =>
  addressComponents.reduce<string[]>(
    (allTypes: string[], { types }: { types: string[] }) =>
      allTypes.concat(types),
    []
  );

/**
 * A list of address component types we can use to know whether something is a city
 * i.e The most common used is locality, If absent we use postal_town or else we use
 * administrative_area_level_2
 */
const CITY_IDENTIFIERS = [
  'locality',
  'postal_town',
  'neighborhood',
  'administrative_area_level_2',
  'sublocality_level_1',
];

const getCityIdentifier = (addressComponents: AddressComponents) => {
  const componentTypes = getAddressComponentsTypes(addressComponents);
  return CITY_IDENTIFIERS.find((identifier) =>
    componentTypes.includes(identifier)
  );
};

const createLocationDetailValidator =
  (type: string) =>
  (addressComponent: {
    types: string[];
    long_name: string;
    short_name: string;
  }) =>
    addressComponent.types.includes(type);

const isState = createLocationDetailValidator('administrative_area_level_1');
const isCountry = createLocationDetailValidator('country');

export const getCityAndCountry = (addressComponents: AddressComponents) => {
  let country: string = '';
  let city: string = '';
  let state: string = '';
  const cityIdentifier = getCityIdentifier(addressComponents);
  const isCity = createLocationDetailValidator(cityIdentifier!);

  addressComponents.forEach((addressComponent) => {
    if (isCity(addressComponent)) {
      city = addressComponent.long_name;
    } else if (isState(addressComponent)) {
      state = addressComponent.long_name;
    } else if (isCountry(addressComponent)) {
      country = addressComponent.long_name;
    }
  });

  return { country, state, city };
};

export const getAddressDataFromPlaceDetails = (
  placeDetails: PlaceDetails,
  isCustom: boolean
): AddressFormData => {
  const locationDetails = getCityAndCountry(placeDetails.address_components!);

  const addressFormData: AddressFormData = {
    fullAddress: placeDetails.formatted_address ?? '',
    addressSource: isCustom ? 'custom' : 'google',
    addressLatitude: placeDetails.geometry?.location.lat(),
    addressLongitude: placeDetails.geometry?.location.lng(),
    addressUTCOffset: placeDetails.utc_offset_minutes,
    addressCity: locationDetails.city,
    addressCountry: locationDetails.country,
    name: placeDetails.name,
    ...(isCustom
      ? { addressGoogleAddressId: placeDetails.place_id }
      : { addressGooglePlaceId: placeDetails.place_id }),
  };

  return addressFormData;
};
